import { useState } from 'react'

import { useReactiveVar } from '@apollo/client'
import {
  Button,
  Checkbox,
  Dropdown,
  Modal,
  ModalProvider,
  SquareDotLoadingIndicator,
} from '@joor/design-system'
import styled from 'styled-components'

import usePortal from 'hooks/usePortal'
import { useRenderToasters } from 'hooks/useRenderToasters'

import { refreshOrderDetailsWidget } from '../../review.store'
import { useAssignRetailerMutation } from './assignRetailerModal.mutations'
import { useConnectedRetailers } from './assignRetailerModal.queries'
import { useOrderId } from 'features/Orders/orders.hooks'

const StyledModal = styled(Modal)`
  width: 600px;
  overflow: visible;
  & > div:nth-child(2) {
    overflow: visible;
  }
`

const StyledModalContent = styled.div`
  overflow: visible;
`
const StyledModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  order: 3;
  padding: 24px 24px 8px 24px;
  margin-top: 10px;
  border-top: 1px solid var(--color-primary-200);
  gap: 10px;
`

const LoaderContainer = styled.div`
  width: 565px;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledDropdown = styled(Dropdown)`
  width: 283px;
  margin-bottom: 16px;
`

type AssignRetailerModalProps = {
  buyerEmail: string
  onCloseModal: () => void
}

type DropdownOption = { id: string; value: string }

const AssignRetailerModal = ({
  buyerEmail,
  onCloseModal,
}: AssignRetailerModalProps) => {
  const Portal = usePortal()
  const orderId = useOrderId()
  const { renderSuccessToast, renderErrorToast } = useRenderToasters()
  const { mutation, loading: mutationLoading } = useAssignRetailerMutation()
  const [selectedRetailer, setSelectedRetailer] =
    useState<DropdownOption | null>(null)
  const [keepAddressInfo, setKeepAddressInfo] = useState<boolean>(false)
  const refreshOrderDetailsWidgetRetailer = useReactiveVar(
    refreshOrderDetailsWidget,
  )
  const { data, loading } = useConnectedRetailers()
  const options = data?.connectedAccounts?.edges?.map((edge) => ({
    label: edge.node.displayName,
    value: edge.node.id,
  }))

  return (
    <Portal>
      <ModalProvider>
        <StyledModal
          isOpen
          onClose={onCloseModal}
          title={{
            text: 'Assign Retailer Account',
          }}
          size="medium"
        >
          {loading ? (
            <LoaderContainer>
              <SquareDotLoadingIndicator />
            </LoaderContainer>
          ) : (
            <StyledModalContent>
              <StyledDropdown
                alLowicons
                errorMessage=""
                label="Select a retailer"
                menuPlacement="bottom"
                onClear={() => {}}
                onDeselect={() => {}}
                onSelect={(option: { selected: DropdownOption }) => {
                  if (option) {
                    setSelectedRetailer(option.selected)
                  }
                }}
                options={options}
                placeholder="Select"
                size="NORMAL"
              />
              <Checkbox
                id="overrideAddressCheckbox"
                label="Keep address information"
                onChange={() => setKeepAddressInfo(!keepAddressInfo)}
                checked={keepAddressInfo}
                size="regular"
              />
            </StyledModalContent>
          )}
          <StyledModalActions>
            <Modal.CloseButton>Cancel</Modal.CloseButton>
            <Button
              type="submit"
              disabled={selectedRetailer === null}
              loading={mutationLoading}
              onClick={() => {
                mutation(
                  orderId,
                  selectedRetailer?.value ?? '',
                  buyerEmail,
                  keepAddressInfo,
                )
                  .then((res) => {
                    if (res?.errors?.length && res?.errors?.length > 0) {
                      onCloseModal()
                      renderErrorToast({
                        description:
                          'There was an error while trying to assign the retailer. Please try again.',
                      })
                      return
                    }
                    onCloseModal()
                    refreshOrderDetailsWidgetRetailer &&
                      refreshOrderDetailsWidgetRetailer()
                    renderSuccessToast({
                      description: 'The retailer was assigned successfully.',
                    })
                  })
                  .catch(() => {
                    onCloseModal()
                    renderErrorToast({
                      description:
                        'There was an error while trying to assign the retailer. Please try again.',
                    })
                  })
              }}
            >
              Assign
            </Button>
          </StyledModalActions>
        </StyledModal>
      </ModalProvider>
    </Portal>
  )
}

export default AssignRetailerModal
